import React, { useState, memo } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  ArrowRight,
  Bot,
  Link as LinkIcon,
  Zap,
  Building2,
  BarChart3,
  MessageSquare,
  Sparkles,
  FileEdit,
  FlaskConical,
  FileSearch,
  FileText,
  Folder,
  Home,
  Search
} from 'lucide-react';

const Dashboard = memo(function Dashboard({ onTaskSelect }) {
  const [showVision, setShowVision] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);

  const automationTasks = [
    {
      id: 'lmcu-refi',
      title: 'Create Refinance Order',
      description: 'Upload order PDF. Agentic Sunny extracts property and borrower information with multi-AI consensus, verifies data against DataTrace public records, pre-fills the order form with color-coded confidence indicators, and optionally creates the order in SoftPro.',
      icon: Building2,
      status: 'active',
      features: [
        'Multi-AI consensus extraction',
        'DataTrace property verification',
        'Color-coded confidence indicators',
        'Optional SoftPro order creation'
      ]
    }
  ];

  const toolTasks = [
    {
      id: 'usage-kpis',
      title: 'Usage KPIs Dashboard',
      description: 'View comprehensive analytics and performance metrics for order processing. Track daily orders, API usage, success rates, and detailed transaction history with interactive charts and admin tools.',
      icon: BarChart3,
      status: 'active',
      features: [
        'Daily orders entered tracking',
        'Interactive charts & visualizations',
        'Success rate analytics',
        'Admin view with detailed tables'
      ]
    },
    {
      id: 'chat-test',
      title: 'Chat with Agentic Sunny',
      description: 'Experience our AI-powered property assistant. Ask questions about properties, title searches, and get intelligent responses powered by GPT-4, Claude 3.5 Sonnet, and Gemini with multi-AI consensus.',
      icon: MessageSquare,
      status: 'active',
      features: [
        'Multi-AI consensus responses',
        'Property-aware conversations',
        'Real-time confidence scoring',
        'Token & cost tracking'
      ]
    },
    {
      id: 'datatrace-tester',
      title: 'DataTrace API Tester',
      description: 'Test different DataTrace API products and endpoints. Experiment with property_data, tax, and other products. Configure product codes, types, and endpoints to see raw API responses.',
      icon: FlaskConical,
      status: 'active',
      features: [
        'Test multiple API products',
        'Configure endpoints & types',
        'View raw request/response',
        'Compare different APIs'
      ]
    },
    {
      id: 'titlepoint',
      title: 'TitlePoint API Tester',
      description: 'Complete TitlePoint SOAP API integration with automated workflow and manual testing. Submit property searches, poll for completion, view transaction logs, and test custom XML requests.',
      icon: FileSearch,
      status: 'active',
      features: [
        'Automated workflow execution',
        'Manual API testing',
        'Transaction logging',
        'Custom XML support'
      ]
    }
  ];

  const legacyTasks = [
    {
      id: 'accelerate-integration',
      title: 'Stewart Accelerate Integration Mock-Up',
      description: 'Upload order PDFs or Accelerate report PDFs to auto-fill Stewart Accelerate order forms. Demonstrates how Sunny AIQ can complement Stewart Accelerate with intelligent data extraction and SoftPro integration.',
      icon: Zap,
      status: 'active',
      features: [
        'PDF upload with AI auto-fill',
        'Order & Accelerate report support',
        'DataTrace property verification',
        'Create orders in SoftPro'
      ]
    },
    {
      id: 'single-pdf',
      title: 'Create Order from Purchase Agreement',
      description: 'Upload a purchase agreement PDF from your customer. Agentic Sunny will extract property and order information, get consensus from multiple AI providers, and pre-fill your title order form.',
      icon: FileText,
      status: 'active',
      externalUrl: process.env.REACT_APP_ANALYSIS_API_URL || 'https://sunny-analysis.messagesimproved.com/',
      features: [
        'Single PDF upload',
        'Multi-AI consensus analysis',
        'Auto-fill order fields',
        'Review and edit before submission'
      ]
    },
    {
      id: 'gemini-extract',
      title: 'Gemini Visual Extraction',
      description: 'Use Google Gemini\'s advanced visual AI to extract text and data from any document, including hard-to-read handwriting, scanned documents, and complex forms.',
      icon: Sparkles,
      status: 'active',
      features: [
        'Industry-leading visual AI',
        'Handwriting recognition',
        'Scanned document processing',
        'Structured data extraction'
      ]
    },
    {
      id: 'file-renamer',
      title: 'AI Document Renamer',
      description: 'Automatically rename recorded documents for SoftPro ingestion. AI analyzes PDFs to extract Instrument Numbers, Document Types, and Recording Dates to generate proper filenames.',
      icon: FileEdit,
      status: 'active',
      features: [
        'Bulk PDF processing',
        'AI-powered document analysis',
        'SoftPro naming conventions',
        'Instant file downloads'
      ]
    },
    {
      id: 'multi-source',
      title: 'Summarize Order from Multiple Sources',
      description: 'Drag and drop multiple files or folders, or copy and paste text from various sources. Agentic Sunny will analyze all inputs, get consensus from multiple AI providers, and create a comprehensive order summary for the examiner.',
      icon: Folder,
      status: 'coming',
      features: [
        'Multi-file and folder upload',
        'Text paste support',
        'Cross-document analysis',
        'Comprehensive order summary'
      ]
    },
    {
      id: 'datatrace',
      title: 'DataTrace Property Search',
      description: 'Search property information using DataTrace API. Enter an address to retrieve comprehensive property details, public records, tax information, and ownership history.',
      icon: Home,
      status: 'active',
      features: [
        'Address-based property lookup',
        'Tax & assessment data',
        'Ownership history',
        'Public records retrieval'
      ]
    },
    {
      id: 'property-lookup',
      title: 'Check Public Property Information',
      description: 'Query title vendor APIs to retrieve public records and information about a property. Get instant access to property history, liens, encumbrances, and other public data.',
      icon: Search,
      status: 'coming',
      features: [
        'Title vendor API integration',
        'Public records search',
        'Property history lookup',
        'Automated data retrieval'
      ]
    }
  ];

  const renderTaskCard = (task) => (
    <Card
      key={task.id}
      className={`transition-all hover:shadow-lg ${
        task.status === 'coming' ? 'opacity-60' : 'cursor-pointer hover:border-primary/50'
      }`}
      onClick={() => {
        if (task.status === 'active') {
          if (task.externalUrl) {
            window.open(task.externalUrl, '_blank');
          } else {
            onTaskSelect(task.id);
          }
        }
      }}
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="text-primary">
            <task.icon className="h-8 w-8" />
          </div>
          <div className="flex gap-2">
            {task.status === 'coming' && (
              <Badge variant="secondary">Coming Soon</Badge>
            )}
            {task.externalUrl && (
              <Badge variant="outline" className="gap-1">
                <ExternalLink className="h-3 w-3" />
                External
              </Badge>
            )}
          </div>
        </div>
        <CardTitle className="text-xl mt-4">{task.title}</CardTitle>
        <CardDescription className="text-sm leading-relaxed">
          {task.description}
        </CardDescription>
      </CardHeader>

      <CardContent>
        <ul className="space-y-2">
          {task.features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
              <span className="text-primary mt-0.5">•</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>

      {task.status === 'active' && (
        <CardFooter>
          <Button className="w-full" variant="default">
            {task.externalUrl ? (
              <>
                Open App
                <ExternalLink className="ml-2 h-4 w-4" />
              </>
            ) : (
              <>
                Get Started
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </CardFooter>
      )}
    </Card>
  );

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Intro Section */}
      <div className="mb-12 text-center space-y-4">
        <h2 className="text-4xl font-bold tracking-tight">Welcome to the Agentic Sunny Lab</h2>
        <p className="text-xl text-muted-foreground">
          Alpha testing environment for AI-powered title tools
        </p>
        <p className="text-base text-muted-foreground max-w-3xl mx-auto leading-relaxed">
          This is our working laboratory where we assemble and showcase early versions of AI-powered tools for Sun Title employees. Test the latest automation workflows and utilities as we build the future of title examination. Your feedback shapes what comes next.
        </p>
      </div>

      {/* Automation Section */}
      <div className="mb-12">
        <div className="mb-6">
          <h3 className="text-2xl font-semibold mb-2">Automation</h3>
          <p className="text-muted-foreground">
            Agentic workflows that execute end-to-end processes with human oversight at critical decision points
          </p>
        </div>

        {/* Vision Toggle */}
        <Card className="mb-6">
          <CardHeader>
            <Button
              variant="ghost"
              className="w-full justify-start text-left font-semibold"
              onClick={() => setShowVision(!showVision)}
            >
              {showVision ? (
                <ChevronDown className="mr-2 h-4 w-4" />
              ) : (
                <ChevronRight className="mr-2 h-4 w-4" />
              )}
              Full Automation Vision
            </Button>
          </CardHeader>

          {showVision && (
            <CardContent className="space-y-6">
              <div>
                <h4 className="text-xl font-semibold mb-2">Sunny Agentic AI Overview</h4>
                <p className="text-sm text-muted-foreground mb-1">
                  <strong>Task:</strong> Order Entry & Title Exam Automation
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong>Initial Scope:</strong> LMCU Residential Refinance in Kent & Ottawa Counties
                </p>
              </div>

              <Card className="bg-muted/50">
                <CardContent className="pt-6">
                  <p className="text-sm leading-relaxed">
                    Online web portal that acts as a standalone middleware powered by Sunny as an agentic AI that
                    automates/streamlines the order process from initiation to commitment. By capturing order details
                    from online forms and emails, Sunny automates tax verification and title searches. Should any
                    discrepancies arise, a Sun Title employee will be prompted to provide a decision.
                  </p>
                </CardContent>
              </Card>

              <div className="grid gap-4 md:grid-cols-2">
                {[
                  {
                    title: '1. Order Entry',
                    content: 'Order Entry information will be extracted by OCR and submitted to multiple AI models for analysis to automatically extract the data and thereby eliminating the need for manual data entry. By having a consensus of models reviewing the information, it ensures discrepancies are elevated and evaluated by a human. The information will be cross-checked with the public records data from DataTrace to verify that the property details are consistent with the order submitted.'
                  },
                  {
                    title: '2. Tax Verification',
                    content: 'With the order now created in SoftPro the tax verification can be completed. Within the Sunny Portal an order for tax verification will be submitted to DataTrace and the results will include all needed information. If there are any specials or discrepancies, Sunny will provide a call out for further investigation by the employee.'
                  },
                  {
                    title: '3. Title Search',
                    content: 'Check DataTrace for the recordable documents related to the property, push to SP, review documents to see if the information in the document(s) needs to be included in the commitment. Will also search Sun Title\'s historical information for priors and if there are any discrepancies, it will bring in the human for evaluation.'
                  },
                  {
                    title: '4. Commitment Generation',
                    content: 'With all information validated and entered in SoftPro, Sunny will generate the commitment and deliver to the relevant parties on the order.'
                  }
                ].map((phase, index) => (
                  <Card key={index}>
                    <CardHeader>
                      <CardTitle className="text-base">{phase.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground leading-relaxed">{phase.content}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          )}
        </Card>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {automationTasks.map(renderTaskCard)}
        </div>
      </div>

      {/* Tools Section */}
      <div className="mb-12">
        <div className="mb-6">
          <h3 className="text-2xl font-semibold mb-2">Tools</h3>
          <p className="text-muted-foreground">
            Standalone AI utilities to enhance your daily workflow
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {toolTasks.map(renderTaskCard)}
        </div>
      </div>

      {/* Mockups & Legacy Section */}
      <div className="mb-12">
        <div className="mb-6">
          <h3 className="text-2xl font-semibold mb-2">Mockups & Legacy</h3>
          <p className="text-muted-foreground">
            Demonstration mockups and earlier prototypes for reference
          </p>
        </div>

        <Card>
          <CardHeader>
            <Button
              variant="ghost"
              className="w-full justify-start text-left font-semibold"
              onClick={() => setShowLegacy(!showLegacy)}
            >
              {showLegacy ? (
                <ChevronDown className="mr-2 h-4 w-4" />
              ) : (
                <ChevronRight className="mr-2 h-4 w-4" />
              )}
              Show Mockups & Legacy
            </Button>
          </CardHeader>

          {showLegacy && (
            <CardContent>
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {legacyTasks.map(renderTaskCard)}
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
});

export default Dashboard;
